/** Inland swamp cypress: flared living roots, an open trunk and irregular
 * elevated crowns. One botanical plan feeds both tessellations and wind. */
import * as T from 'three';
import {range,type Rng} from '@hyperlight/shared';
import type {BuildCtx,C3,SpeciesParams} from './types';
import {V,shade} from './primitives';
import {TREE_STEM,NO_JOINT,windJoint,type WindJoint} from './treeWind';

interface Knot {p:T.Vector3;radius:number}
interface Crown {center:T.Vector3;radius:number;depth:number;phase:number;lobes:number[];tone:number}
interface Moss {root:T.Vector3;length:number;width:number;phase:number;tone:number}
interface Limb {path:Knot[];twigs:Knot[][];crowns:Crown[];moss:Moss[]}
interface Root {angle:number;attach:T.Vector3;toe:T.Vector3;knee:T.Vector3;reach:number;width:number;height:number}
export interface CypressPlan {stem:Knot[];roots:Root[];limbs:Limb[];height:number;footprint:number}

export function planCypress(rng:Rng,sp:SpeciesParams,maturity:number):CypressPlan {
  const grow=.45+.75*maturity,girth=.65+.6*maturity,h=range(rng,...sp.trunk.height)*grow,r=sp.trunk.radius*girth;
  const phase=rng()*Math.PI*2,out=V(Math.cos(phase),0,Math.sin(phase)),side=V(-out.z,0,out.x);
  const stem=[{p:V(0,-.28,0),radius:r*1.48},{p:out.clone().multiplyScalar(.12*grow).add(V(0,h*.09,0)),radius:r*.95},
    {p:out.clone().multiplyScalar(-h*.022).add(V(0,h*.29,0)),radius:r*.84},
    {p:out.clone().multiplyScalar(h*.041).addScaledVector(side,h*.013).add(V(0,h*.50,0)),radius:r*.68},
    {p:out.clone().multiplyScalar(h*.071).add(V(0,h*.72,0)),radius:r*.42}];
  const roots:Root[]=[];
  for(let i=0;i<6;i++){
    const angle=phase+i*Math.PI/3+range(rng,-.15,.15),direction=V(Math.cos(angle),0,Math.sin(angle));
    const reach=range(rng,2.7,3.3)*grow;
    roots.push({angle,attach:stem[0].p.clone().addScaledVector(direction,r*.26).add(V(0,range(rng,2.8,3.7)*grow,0)),
      toe:direction.clone().multiplyScalar(reach).add(V(0,-.22,0)),
      knee:direction.clone().multiplyScalar(reach*.82).add(V(0,-.12,0)),reach,width:range(rng,.40,.64)*girth,height:range(rng,.85,1.45)*grow});
  }
  const limbs:Limb[]=[];
  for(let i=0;i<4;i++){
    const angle=phase+[.25,2.14,4.05,5.3][i]+range(rng,-.4,.4),axis=V(Math.cos(angle),0,Math.sin(angle)),cross=V(-axis.z,0,axis.x);
    // A heavy central column carries rising scaffold limbs. Their outer
    // sections flatten beneath the crown, giving the moss a readable bough.
    const root=stem[i===3?3:2].p.clone().lerp(stem[i===3?4:3].p,range(rng,i===3?.35:.06,i===3?.92:.55));
    const reach=h*range(rng,.20,.29)*(i===3?.48:1),tip=root.clone().addScaledVector(axis,reach);
    tip.y=h*([.68,.80,.73,.92][i]+range(rng,-.055,.045));
    const elbow=root.clone().lerp(tip,range(rng,.38,.59)).addScaledVector(cross,range(rng,-1.4,1.4)*grow);elbow.y=tip.y-range(rng,.8,1.7)*grow;
    const path=[{p:root,radius:r*.58},{p:elbow,radius:r*.36},{p:tip,radius:r*.15}],crowns:Crown[]=[],twigs:Knot[][]=[],moss:Moss[]=[];
    for(let j=0;j<3;j++){
      const radius=range(rng,...sp.canopy.size)*grow*(i===3?.79:1)*(j===2?.83:j?.89:1)*1.12;
      const center=(j===2?elbow.clone().lerp(tip,.48):tip.clone()).addScaledVector(axis,j===1?-radius*.90:0).addScaledVector(cross,j===1?radius*.28:j===2?-radius*.22:0);
      center.y+=grow*(j===2?-.14:j===1?-.55:.10)+range(rng,-.3,.3)*grow;
      const twigRoot=j===2?elbow.clone().lerp(tip,.25):j?elbow.clone().lerp(tip,.53):tip.clone();
      const twigTip=center.clone().addScaledVector(axis,radius*.60).add(V(0,-.22*grow,0));
      twigs.push([{p:twigRoot,radius:r*.13},{p:twigTip,radius:r*.045}]);
      crowns.push({center,radius,depth:range(rng,2.9,4.0)*grow*(j===2?.84:1),phase:angle+range(rng,-.3,.3),
        lobes:Array.from({length:12},()=>range(rng,.76,1.20)),tone:range(rng,.85,1.12)});
      if(j===0||rng()<.5){
        // Moss attaches to its living twig, within the crown. Each cluster
        // shares that twig's transform; there are no independently floating strands.
        const root=twigRoot.clone().lerp(twigTip,.92);
        moss.push({root,length:Math.min(range(rng,4.8,6.5)*grow,Math.max(.6*grow,root.y-1.6*grow)),width:range(rng,1.4,2.2)*grow,phase:angle+range(rng,-.5,.5),tone:range(rng,.86,1.04)});
      } else moss.push({root:twigTip.clone(),length:0,width:0,phase:angle,tone:1});
    }
    limbs.push({path,twigs,crowns,moss});
  }
  const height=Math.max(...limbs.flatMap(b=>b.crowns.map(c=>c.center.y+c.depth*.65)));
  return {stem,roots,limbs,height,footprint:Math.max(...roots.map(root=>root.reach+root.width*.2))};
}

class CypressMesh {
  p:number[]=[];color:number[]=[];sway:number[]=[];branch:number[]=[];twig:number[]=[];
  joint:WindJoint=TREE_STEM;child:WindJoint=NO_JOINT;
  tri(a:T.Vector3,b:T.Vector3,c:T.Vector3,color:C3,sway=0):void {
    for(const p of [a,b,c]){this.p.push(p.x,p.y,p.z);this.color.push(...color);this.sway.push(sway);this.branch.push(...this.joint);this.twig.push(...this.child);}
  }
  /** Closed folded leaf: every viewing direction sees the same attached
   * ribbon without a separate double-sided material or depth program. */
  leaf(root:T.Vector3,tip:T.Vector3,across:T.Vector3,width:number,bulge:T.Vector3,color:C3,widest=.48):void {
    const mid=root.clone().lerp(tip,widest),left=mid.clone().addScaledVector(across,-width),right=mid.clone().addScaledVector(across,width),ridge=mid.clone().add(bulge);
    const center=root.clone().add(tip).add(left).add(right).add(ridge).multiplyScalar(.2);
    for(const [a,b,c]of [[root,left,ridge],[left,tip,ridge],[tip,right,ridge],[right,root,ridge],[root,right,tip],[root,tip,left]]){
      const outward=b.clone().sub(a).cross(c.clone().sub(a)).dot(a.clone().add(b).add(c).multiplyScalar(1/3).sub(center));
      if(outward>=0)this.tri(a,b,c,color,1);else this.tri(a,c,b,color,1);
    }
  }
  wood(path:Knot[],color:C3,sides:number):void {
    const rings=path.map((k,i)=>{
      const axis=path[Math.min(i+1,path.length-1)].p.clone().sub(path[Math.max(0,i-1)].p).normalize(),q=new T.Quaternion().setFromUnitVectors(V(0,1,0),axis);
      return Array.from({length:sides},(_,s)=>V(Math.cos(s*Math.PI*2/sides)*k.radius,0,Math.sin(s*Math.PI*2/sides)*k.radius).applyQuaternion(q).add(k.p));
    });
    for(let s=0;s<sides;s++){
      const n=(s+1)%sides;
      this.tri(path[0].p,rings[0][s],rings[0][n],color);
      for(let i=0;i<path.length-1;i++){
        const pigment=shade(color,.88+.06*((i+s)%4));
        this.tri(rings[i][s],rings[i+1][s],rings[i][n],pigment);this.tri(rings[i][n],rings[i+1][s],rings[i+1][n],pigment);
      }
      this.tri(path.at(-1)!.p,rings.at(-1)![n],rings.at(-1)![s],color);
    }
  }
  root(root:Root,color:C3,far:boolean):void {
    const across=V(-Math.sin(root.angle),0,Math.cos(root.angle)),middle=root.attach.clone().lerp(root.toe,.57);middle.y=.34;
    const a=root.attach.clone().addScaledVector(across,-root.width*.30),b=root.attach.clone().addScaledVector(across,root.width*.30),
      l=root.toe.clone().addScaledVector(across,-root.width*.40),r=root.toe.clone().addScaledVector(across,root.width*.40),
      foot=root.attach.clone().setY(-.25);
    // Wide triangular flares join a substantial trunk. They are neither stilts
    // nor disconnected roots hovering over the ground.
    // Shadow normal bias must move out of this broad root, not into it.
    this.tri(a,middle,l,shade(color,.91));this.tri(a,b,middle,color);this.tri(b,r,middle,shade(color,1.04));
    this.tri(l,middle,r,shade(color,.96));this.tri(a,l,foot,shade(color,.82));this.tri(b,foot,r,shade(color,.87));
    this.tri(foot,l,r,shade(color,.8));
    // Knees grow from that same wide root. Their bases intersect its grounded
    // envelope, so a low-poly spike never reads as an independent stump.
    this.wood([{p:root.knee,radius:root.width*.40},{p:root.knee.clone().add(V(.07,root.height,0)),radius:.025}],shade(color,.92),far?3:5);
  }
  crown(c:Crown,color:C3,far:boolean):void {
    const sides=far?6:12;
    const ring=(scale:number,y:number)=>Array.from({length:sides},(_,i)=>{
      const k=i*12/sides,a=c.phase+k*Math.PI/6;return c.center.clone().add(V(Math.cos(a)*c.radius*c.lobes[k]*scale,y*c.depth+(c.lobes[(k+3)%12]-1)*c.depth*.40,Math.sin(a)*c.radius*c.lobes[k]*scale));
    });
    const rings=far?[ring(1,0)]:[ring(.65,-.36),ring(1,0),ring(.63,.38)],top=c.center.clone().add(V(.13,c.depth*.64,-.08)),bottom=c.center.clone().add(V(0,-c.depth*.50,0));
    for(let i=0;i<sides;i++){
      const n=(i+1)%sides,tone=shade(color,.89+.045*(i%5));
      this.tri(bottom,rings[0][i],rings[0][n],shade(tone,.91),1);
      for(let j=0;j<rings.length-1;j++){this.tri(rings[j][i],rings[j+1][i],rings[j][n],tone,1);this.tri(rings[j][n],rings[j+1][i],rings[j+1][n],tone,1);}
      this.tri(top,rings.at(-1)![n],rings.at(-1)![i],tone,1);
      if(!far){
        const edge=rings[1][i],edge2=rings[1][n],p=edge.clone().lerp(edge2,.48).lerp(c.center,-.13).add(V(0,-.15,0));
        this.tri(edge,p,edge2,shade(tone,1.02),1);
      }
    }
    // Overlapping subordinate sprays hide the smooth polygon-shell outline.
    // Their shared roots lie inside the main mass; pointed closed leaf fans
    // break the edge at a smaller botanical scale, not with random triangles.
    for(let k=0;k<8;k++){
      if(far&&k%3)continue;
      const a=c.phase+k*Math.PI/4,along=V(Math.cos(a),0,Math.sin(a)),across=V(-along.z,0,along.x);
      const centre=c.center.clone().addScaledVector(along,c.radius*(.77+.10*c.lobes[k]));
      centre.y+=c.depth*[-.24,.20,-.10,.34,-.30,.14,-.06,.27][k];
      const radius=c.radius*(.34+.10*c.lobes[(k+2)%12]),depth=c.depth*(.37+.08*c.lobes[k]),sides=far?3:5;
      const ring=Array.from({length:sides},(_,i)=>{const t=a+i*Math.PI*2/sides;return centre.clone().add(V(Math.cos(t)*radius,(i%2?-.10:.07)*depth,Math.sin(t)*radius));});
      const top=centre.clone().add(V(radius*.14,depth*.58,-radius*.10)),bottom=centre.clone().add(V(-radius*.1,-depth*.58,radius*.08));
      const pigment=shade(color,.90+.12*c.lobes[k]);
      for(let j=0;j<sides;j++){const n=(j+1)%sides;this.tri(bottom,ring[j],ring[n],pigment,1);this.tri(top,ring[n],ring[j],pigment,1);}
      if(!far)for(const sign of [-1,1]){
        const root=centre.clone().addScaledVector(along,radius*.24).addScaledVector(across,sign*radius*.24),tip=root.clone().addScaledVector(along,radius*.54).addScaledVector(across,sign*radius*.28).add(V(0,-depth*.16,0));
        this.leaf(root,tip,across,radius*.22,V(0,radius*.13,0),shade(pigment,sign<0?.93:1.04));
      }
    }
  }
  moss(m:Moss,color:C3,far:boolean):void {
    if(!m.length)return;
    const side=V(Math.cos(m.phase),0,Math.sin(m.phase)),front=V(-side.z,0,side.x);
    // Four unequal straps share one anchored cluster. The short inner strand
    // disappears at distance; the other three keep their exact shape/position.
    // Shape phases consume no botanical RNG and leave the crowns/roots intact.
    for(let i=0;i<4;i++){
      if(far&&i===2)continue;
      const phase=m.phase*2.37+i*2.71,shape=Math.sin(phase),curl=Math.sin(phase*1.43+.8);
      const root=m.root.clone().addScaledVector(side,([-.43,-.14,.19,.43][i]+shape*.025)*m.width);
      const drop=m.length*([.70,.95,.45,.81][i]+shape*.045);
      const tip=root.clone().addScaledVector(side,curl*m.width*.12)
        .addScaledVector(front,m.width*(.10+shape*.13)).add(V(0,-drop,0));
      // Narrow gaps, mixed drops and offset tips avoid a solid regularly
      // toothed sheet. The width remains high enough to read as hanging moss.
      const width=m.width*[.12,.18,.09,.15][i]*(.94+curl*.10);
      this.leaf(root,tip,side,width,front.clone().multiplyScalar(-m.width*(.075+.025*shape)),
        shade(color,.94+shape*.045),.28+curl*.09);
    }
  }
  finish():T.BufferGeometry {
    const g=new T.BufferGeometry();for(const [name,data,size] of [['position',this.p,3],['color',this.color,3],['sway',this.sway,1],['branchWind',this.branch,4],['twigWind',this.twig,4]] as const)g.setAttribute(name,new T.Float32BufferAttribute(data,size));
    g.computeVertexNormals();return g;
  }
}

export function buildCypress(rng:Rng,sp:SpeciesParams,ctx:BuildCtx,far=false):T.BufferGeometry {
  const plan=planCypress(rng,sp,ctx.maturity),mesh=new CypressMesh();
  const mossColor:C3=[sp.palette.leafSummer[0]*.9+.09,sp.palette.leafSummer[1]*.8+.06,sp.palette.leafSummer[2]*.7+.075];
  mesh.wood(plan.stem,sp.palette.bark,far?4:8);
  for(const root of plan.roots)mesh.root(root,sp.palette.bark,far);
  for(const limb of plan.limbs){
    mesh.joint=windJoint(limb.path[0].p,limb.path.at(-1)!.p,limb.path[0].radius,.66);mesh.child=NO_JOINT;
    mesh.wood(limb.path,sp.palette.bark,far?3:6);
    for(let i=0;i<limb.crowns.length;i++){
      const twig=limb.twigs[i];mesh.child=windJoint(twig[0].p,twig[1].p,twig[0].radius,.66);
      if(!far)mesh.wood(twig,sp.palette.bark,4);
      mesh.crown(limb.crowns[i],shade(sp.palette.leafSummer,limb.crowns[i].tone),far);
      mesh.moss(limb.moss[i],shade(mossColor,limb.moss[i].tone),far);
    }
  }
  const geo=mesh.finish(),path=(ks:Knot[])=>ks.map(k=>({p:k.p.toArray(),radius:k.radius}));
  geo.userData.cypress={stem:path(plan.stem),height:plan.height,footprint:plan.footprint,
    roots:plan.roots.map(r=>({attach:r.attach.toArray(),toe:r.toe.toArray(),knee:r.knee.toArray(),height:r.height,width:r.width})),
    branches:plan.limbs.map(l=>({path:path(l.path),twigs:l.twigs.map(path),crowns:l.crowns.map(c=>({center:c.center.toArray(),radius:c.radius,depth:c.depth})),
      moss:l.moss.map(m=>({root:m.root.toArray(),length:m.length,width:m.width}))}))};
  return geo;
}

/** Folded rush leaves and a few stout cattails, sharing each stalk's wind joint. */
export function buildWetlandReeds(rng:Rng,far=false):T.BufferGeometry {
  const mesh=new CypressMesh(),leaf:C3=[.25,.29,.065],stalk:C3=[.28,.255,.11];
  const plan=Array.from({length:16},()=>({angle:rng()*Math.PI*2,x:range(rng,-.50,.50),z:range(rng,-.50,.50),h:range(rng,.85,1.8),lean:range(rng,.12,.46),width:range(rng,.065,.13)}));
  for(let i=0;i<plan.length;i++){
    const p=plan[i];if(far&&i%2)continue;
    const side=V(Math.cos(p.angle),0,Math.sin(p.angle)),front=V(-side.z,0,side.x),root=V(p.x,-.035,p.z),tip=root.clone().add(V(0,p.h,0)).addScaledVector(front,p.lean);
    mesh.joint=windJoint(root,tip,.028,.48);
    mesh.leaf(root,tip,side,p.width,front.clone().multiplyScalar(-p.width*.35),leaf);
    if(i<3){const head=root.clone().add(V(.04,p.h+.35,0));mesh.wood([{p:root,radius:.026},{p:head,radius:.020}],stalk,3);
      mesh.wood([{p:head.clone().add(V(0,-.22,0)),radius:.07},{p:head.clone().add(V(0,.12,0)),radius:.056}],[.245,.17,.085],far?3:4);}
  }
  const g=mesh.finish();g.userData.inlandReeds=true;return g;
}
